<template>
    <div class="w-full h-[calc(100vh-3.5rem)] fixed flex space-y-2 text-xs items-center flex-col bg-gray-50">
        <ReportingDashboard
            v-if="showReportDashboard"
            :today="today"
            :clientId="clientId"
            :clientName="clientName"
            :campaigns="campaigns"
            :campaignOptions="selectedCampaignOptions"
            :clientFeedback="clientFeedback"
            :clientFeedbackOptions="clientFeedbackOptions"
            :companies="companies"
            :contactedCompanies="contactedCompanies"
            :notContactedCompanies="notContactedCompanies"
            :respondedCompanies="respondedCompanies"
            :noResponseCompanies="noResponseCompanies"
            :crmActions="crmActions"
            :allCRMActions="allCRMActions"
            :allCRMStatuses="allCRMStatuses"
            :allActiveCampaigns="allActiveCampaigns"
            :reportOptions="reportOptions"
            :defaultReport="defaultReport"
            @hide-report="hideClientReport"
            @refetch="viewClientReport"
        />
        <ReportSetup
            v-else
            :clientOptions="clientOptions"
            :selectedClientId="clientId"
            :campaignOptions="campaignOptions"
            :selectedCampaigns="campaigns"
            @select-client="setClientId"
            @select-campaigns="setCampaigns"
            @view-report="viewClientReport"
        />
        <transition
            enter-active-class="transition duration-400 ease-in"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition duration-400 ease-out"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
            class="h-full"
        >
            <div v-if="isLoading" class="fixed top-0 bottom-0 left-0 right-0 bg-black/[0.75] flex flex-col justify-center items-center z-40"
                    tabindex="-1" >
                <div class="flex flex-col justify-center items-center p-4 rounded-md">
                <svg aria-hidden="true" class="mx-1 w-8 h-8 text-gray-200 animate-spin dark:text-gray-600"
                    viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                            fill="#d3d3d3"/>
                        <path
                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                            fill="#2E9CCA"/>
                </svg>
                <div class="text-white p-4 whitespace-nowrap text-base font-semibold tracking-wide">
                    Retrieving Company CRM Information
                </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import { useStore } from 'vuex';
import { getClients } from '@/services/Client';
import { getCampaigns } from '@/services/Campaigns';
import { getCRMReports } from '@/services/CRMReport';
import { getCRMReportingCompanies } from '@/services/Company';
import { getActions } from '@/services/CrmAction';
import { getCrmStatuses } from '@/services/CrmStatus';
import ReportSetup from './ReportSetup.vue';
import ReportingDashboard from './ReportingDashboard.vue';
import { getAutoComputedCRMEvent, getDefaultReportedCRMEvent, getComputedCRMStatusByEventId } from '@/composables/CompanyCRMTools';
import { formatDate } from '@/composables/formatDate';
import { getActiveCampaigns } from '@/services/Campaign';

const store = useStore()
const isLoading = ref(false)
const showReportDashboard = ref(false)
const clientOptions = ref([])
const clientId = ref(null)

const companies = ref([])
const filteredCompanies = ref([])
const contactedCompanies = ref([])
const notContactedCompanies = ref([])
const respondedCompanies = ref([])
const noResponseCompanies = ref([])

const campaignOptions = ref([])
const campaigns = ref([])
const allActiveCampaigns = ref([])
const selectedCampaignOptions = ref([])

const clientFeedbackOptions = ref([
    {
        label: 'None',
        value: null,
    },
    {
        label: 'Approved',
        value: 'approved',
    },
    {
        label: 'Excluded',
        value: 'excluded',
    },
    {
        label: 'Hold',
        value: 'hold',
    },
    {
        label: 'Pass',
        value: 'pass',
    },
    {
        label: 'Research Further',
        value: 'research_further',
    },
])

const clientFeedback = ref([])

const crmActions = ref([])
const allCRMActions = ref([])
const allCRMStatuses = ref([])

const closedReportOptions = ref([])
const draftReportOptions = ref([])
const reportOptions = ref([])
const defaultReport = ref(null)

const today = computed(() => {
    const date = new Date()
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
})

watch(() => companies.value, () => {
    filteredCompanies.value = companies.value.filter((company) => campaigns.value.includes(company.campaign.id)).filter((company) => clientFeedback.value.length > 0 ? clientFeedback.value.includes(company.current_client_feedback) : true)
}, {deep: true})

watch(() => campaigns.value, () => {
    filteredCompanies.value = companies.value.filter((company) => campaigns.value.includes(company.campaign.id)).filter((company) => clientFeedback.value.length > 0 ? clientFeedback.value.includes(company.current_client_feedback) : true)
}, {deep: true})

watch(() => clientFeedback.value, () => {
    filteredCompanies.value = companies.value.filter((company) => campaigns.value.includes(company.campaign.id)).filter((company) => clientFeedback.value.length > 0 ? clientFeedback.value.includes(company.current_client_feedback) : true)
}, {deep: true})

watch(() => filteredCompanies.value, () => {
    updateContactedCompanies()
    updateNotContactedCompanies()
})

watch(() => contactedCompanies.value, () => {
    updateRespondedCompanies()
    updateNoResponseCompanies()
})

watch(() => clientId.value, () => {
    if(clientId.value) {
        loadCampaignOptions(clientId.value)
        loadReportOptions(clientId.value)
    } else {
        campaignOptions.value = []
        campaigns.value = []
        draftReportOptions.value = []
        closedReportOptions.value = []
        reportOptions.value = []
        defaultReport.value = null
    }
})


function updateContactedCompanies () {
    contactedCompanies.value = filteredCompanies.value.filter((company) => {
        return company.computed_crm_status.label != 'Not Contacted'
    })
}

function updateNotContactedCompanies () {
    notContactedCompanies.value = filteredCompanies.value.filter((company) => {
        return company.computed_crm_status.label == 'Not Contacted'
    })
}

function updateRespondedCompanies () {
    respondedCompanies.value = contactedCompanies.value.filter((company) => {
        return company.computed_crm_status.label != 'No Response'
    })
}

function updateNoResponseCompanies () {
    noResponseCompanies.value = contactedCompanies.value.filter((company) => {
        return company.computed_crm_status.label == 'No Response'
    })
}

function setClientId(clientValue) {
    clientId.value = clientValue
}

function setCampaigns(campaignIds) {
    campaigns.value = campaignIds
}

function hideClientReport() {
    // console.log('hideClientReport')
    showReportDashboard.value = false
}

const clientName = computed(() => {
    if(clientId.value) {
        let foundClient = clientOptions.value.find((clientOption) => clientOption.value == clientId.value)
        if(foundClient) {
            return foundClient.label
        }
    }
    return null
})

const fetchFilter = computed(() => {
    let query = new URLSearchParams({});

    query.append('client', clientId.value)
    campaigns.value.forEach(campaign => {
        query.append('campaign', campaign)
    })
    query.append('get_all', 'true')
    let url;
    let queriesCount = 0;
    for (let value of query.values()) {
        queriesCount += 1
    }
    if(queriesCount > 0) {
        url = "?" + query.toString();
    } else {
        url = "?"
    }
    return url

})

async function loadClientOptions() {
    const token = store.state.auth.token
    const result = await getClients({token: token, endpoint: '?get_all=true'})
    clientOptions.value = result.data.map((client) => {
        return {value: client.id, label: client.client_name}
    })
}

async function loadCampaignOptions(clientId) {
    const payload = {
        endpoint: `?client=${clientId}`,
        token: store.state.auth.token
    }
    const result = await getCampaigns(payload)
    campaignOptions.value = result.map((campaign) => {
        return {value: campaign.id, id: campaign.id, label: campaign.campaign_name}
    })
    // campaigns.value = [...campaignOptions.value.map((campaign) => {
    //         return campaign.value
    // })]
}

async function loadReportOptions(clientId) {
    const payload = {
        token: store.state.auth.token,
        endpoint: `?client=${clientId}`,
    }
    const results = await getCRMReports(payload)
    reportOptions.value = results.map((report) => {
        return {value: report.id, label: `${formatDate(report.report_date)}: ${report.report_name}`, report: report}
    })
    if(reportOptions.value.length > 0) {
        defaultReport.value = reportOptions.value[0]
    }
    draftReportOptions.value = results.filter((report) => report.status == 'draft').map((report) => {
        return {value: report.id, label: `${formatDate(report.report_date)}: ${report.report_name}`, report: report}
    })
    closedReportOptions.value = results.filter((report) => report.status == 'closed').map((report) => {
        return {value: report.id, label: `${formatDate(report.report_date)}: ${report.report_name}`, report: report}
    })
}

async function loadAllCRMActions() {
    const token = store.state.auth.token
    const response = await getActions(token)
    allCRMActions.value = response.data
}

async function loadAllCRMStatuses(){
    const token = store.state.auth.token
    const result = await getCrmStatuses(token)
    allCRMStatuses.value = result
}

async function viewClientReport() {
    // console.log('viewClientReport')
    isLoading.value = true
    const payload = {
        token: store.state.auth.token,
        endpoint: fetchFilter.value,
    }
    const response = await getCRMReportingCompanies(payload)
    companies.value = response.data.companies.map(assignComputedEventAndStatus).sort((a, b) => {
        return a.computed_crm_status.value - b.computed_crm_status.value
    });
    crmActions.value = response.data.crm_actions.sort(function(a,b) {
        return new Date(b.resolved_date) - new Date(a.resolved_date)
    })
    showReportDashboard.value = true
    selectedCampaignOptions.value = campaignOptions.value.filter((campaign) => campaigns.value.includes(campaign.value))
    loadReportOptions(clientId.value)
    isLoading.value = false
}

function assignComputedEventAndStatus (company) {
    let auto_computed_crm_event = getAutoComputedCRMEvent(company)
    let default_crm_event
    let computed_crm_status
    if(company.auto_compute_reported_crm_event) {
        default_crm_event = auto_computed_crm_event
    } else {
        default_crm_event = getDefaultReportedCRMEvent(company)
    }
    if(default_crm_event) {
        computed_crm_status = getComputedCRMStatusByEventId(default_crm_event.action.id)
    } else {
        computed_crm_status = getComputedCRMStatusByEventId(null)
    }
    return {
        ...company,
        computed_crm_event: auto_computed_crm_event,
        reported_crm_event: default_crm_event,
        default_crm_event: default_crm_event,
        computed_crm_status: computed_crm_status,
        default_auto_compute: company.auto_compute_reported_crm_event,
    }
}

async function loadAllActiveCampaigns(){
    const token = store.state.auth.token
    const result = await getActiveCampaigns(token)
    allActiveCampaigns.value = result
}

loadClientOptions()
loadAllCRMActions()
loadAllCRMStatuses()
loadAllActiveCampaigns()

</script>